import { React, useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Nav, Tab, Col, Spinner } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import brandLogo from "../../assets/images/logo.png";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall } from "../../api/ApiServices";
import { notifyError, notifySuccess } from "../../sharedComponent/notify";
import Loader from "../../sharedComponent/Loader";
import NoDataComp from "../../sharedComponent/NoDataComp";
import { PageHead, KpiGrid, BoolBadge } from "./ffUi";
import { labelFor, formatValue, toDateDisplay } from "./ffHelpers";
import "./FleetFinance.scss";

const animations = {
  initial: { opacity: 0, x: 400 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 },
};

const isNum = (v) => typeof v === "number" || (v !== "" && v !== null && !isNaN(v));
const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (isNum(v)) return Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 });
  return String(v);
};
// U-03: readable labels for raw API keys
const label = (k) => labelFor(k);
// Codes / ids / numbers are identifiers, not money — never add decimals.
const isCodeKey = (k) => /(^|_)(code|no|number)$|(^|_)id$/i.test(String(k));
// U-03 / FF-06: generic cell (Yes/No, money, dd-mm-yyyy); identifiers stay verbatim
const cell = (k, v) => {
  if (v === null || v === undefined || v === "") return "-";
  if (isCodeKey(k)) return String(v);
  return formatValue(v, k);
};
const isDateKey = (k) => /date|as_of|period_end|period_start/i.test(String(k || ""));

// Money formatter: always 2 decimals, "-" for empty, keeps non-numeric strings.
const money = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (isNaN(n)) return String(v);
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
const isNeg = (v) => isNum(v) && Number(v) < 0;

const Badge = ({ ok }) => (
  <span className={`ff-badge ${ok ? "ff-badge-success" : "ff-badge-danger"}`}>
    {ok ? "Balanced" : "Out of balance"}
  </span>
);

// Smart single value: boolean -> badge (Yes/No, or Balanced for *balanced/ok keys),
// number -> money, date -> dd-mm-yyyy, else raw.  (U-03, FF-06)
const valEl = (v, k) => {
  if (typeof v === "boolean")
    return /balanced|(^|_)ok$/i.test(String(k || "")) ? <Badge ok={v} /> : <BoolBadge value={v} />;
  if (v === null || v === undefined || v === "") return "-";
  if (isDateKey(k) && /^\d{4}-\d{2}-\d{2}/.test(String(v))) return toDateDisplay(v);
  if (isNum(v)) return <span className={isNeg(v) ? "ff-neg" : ""}>{money(v)}</span>;
  return String(v);
};

// Common header block (Title / Currency / As Of) shared by the statements.
const StatementHeader = ({ report, extra }) => (
  <div className="ff-section">
    <div className="ff-kv">
      <span>Title</span>
      <b>{report.title || "-"}</b>
    </div>
    <div className="ff-kv">
      <span>Currency</span>
      <b>{report.currency || "-"}</b>
    </div>
    <div className="ff-kv">
      <span>As Of</span>
      <b>{toDateDisplay(report.as_of)}</b>{/* FF-06 */}
    </div>
    {extra}
  </div>
);

// Account-line table (code / name / amount) with an optional bold total row.
const AccountTable = ({ title, rows = [], totalLabel, total }) => (
  <div className="ff-section">
    <p className="ff-section-title">{title}</p>
    <div className="VA_table">
      <table className="table tableAdmin">
        <thead className="tableHead">
          <tr>
            <th>Account Code</th>
            <th>Account Name</th>
            <th className="text-end">Amount</th>
          </tr>
        </thead>
        <tbody className="tableBody">
          {rows.map((row, i) => (
            <tr key={i}>
              <td>{row.account_code}</td>
              <td>{row.account_name}</td>
              <td className={`text-end ${isNeg(row.amount) ? "ff-neg" : ""}`}>
                {money(row.amount)}
              </td>
            </tr>
          ))}
          {total !== undefined && (
            <tr className="ff-total-row">
              <td />
              <td>
                <b>{totalLabel}</b>
              </td>
              <td className={`text-end ${isNeg(total) ? "ff-neg" : ""}`}>
                <b>{money(total)}</b>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

// Emphasised summary line (e.g. Net Income, Total Liabilities & Equity).
const TotalLine = ({ label: lbl, value, badge }) => (
  <div className="ff-total ff-net">
    <span>{lbl}</span>
    {badge !== undefined ? (
      <Badge ok={badge} />
    ) : (
      <b className={isNeg(value) ? "ff-neg" : ""}>{money(value)}</b>
    )}
  </div>
);

// Key/value block for an object (numbers -> money, booleans -> badge).
const KvSection = ({ title, obj }) => (
  <div className="ff-section">
    {title && <p className="ff-section-title">{title}</p>}
    {Object.entries(obj).map(([k, v]) => (
      <div className="ff-kv" key={k}>
        <span>{label(k)}</span>
        <b>{valEl(v, k)}</b>
      </div>
    ))}
  </div>
);

// QA #23: one page — Revenue, Expenses, then Net Income straight after.
const IncomeStatement = ({ r }) => (
  <div className="ff-statement">
    <StatementHeader report={r} />
    <AccountTable
      title="Revenue"
      rows={r.revenue}
      totalLabel="Total Revenue"
      total={r.total_revenue}
    />
    <AccountTable
      title="Expenses"
      rows={r.expenses}
      totalLabel="Total Expenses"
      total={r.total_expenses}
    />
    <TotalLine label="Net Income" value={r.net_income} />
  </div>
);

// QA #22: one page — Assets, Liabilities, Equity, each with its total, then
// Total Liabilities & Equity once at the bottom.
const BalanceSheet = ({ r }) => (
  <div className="ff-statement">
    <StatementHeader
      report={r}
      extra={
        <div className="ff-kv">
          <span>Status</span>
          <b>
            <Badge ok={r.is_balanced} />
          </b>
        </div>
      }
    />
    <AccountTable
      title="Assets"
      rows={r.assets}
      totalLabel="Total Assets"
      total={r.total_assets}
    />
    <AccountTable
      title="Liabilities"
      rows={r.liabilities}
      totalLabel="Total Liabilities"
      total={r.total_liabilities}
    />
    {/* equity = { accounts: [...], retained_earnings_fleet }: one row each */}
    <AccountTable
      title="Equity"
      rows={equityRows(r.equity)}
      totalLabel="Total Equity"
      total={r.total_equity}
    />
    <TotalLine
      label="Total Liabilities & Equity"
      value={r.total_liabilities_and_equity}
    />
  </div>
);

const equityRows = (equity) => {
  if (Array.isArray(equity)) return equity;
  const e = equity || {};
  const rows = Array.isArray(e.accounts) ? [...e.accounts] : [];
  Object.entries(e).forEach(([k, v]) => {
    if (k !== "accounts" && isNum(v)) rows.push({ account_code: "", account_name: label(k), amount: v });
  });
  return rows;
};

const renderCashFlow = (r) => (
  <div className="ff-statement">
    <StatementHeader report={r} />
    <div className="ff-section">
      <p className="ff-section-title">Cash Flows</p>
      <div className="ff-kv">
        <span>Operating Activities</span>
        <b>{valEl(r.operating_activities)}</b>
      </div>
      <div className="ff-kv">
        <span>Investing Activities</span>
        <b>{valEl(r.investing_activities)}</b>
      </div>
      <div className="ff-kv">
        <span>Financing Activities</span>
        <b>{valEl(r.financing_activities)}</b>
      </div>
    </div>
    <TotalLine label="Net Change in Cash" value={r.net_change_in_cash} />
    {r.note && <p className="ff-note">{r.note}</p>}
  </div>
);

// Dashboard value tweaks: readable DTL basis; Total Revenue derived from
// net income + total expenses (the API sends only those two).
const DTL_BASIS = { computed_as_of: "Computed as of" };
const dashboardSection = (key, obj, asOf) => {
  const o = { ...obj };
  if (o.deferred_tax_liability_basis) {
    const txt = DTL_BASIS[o.deferred_tax_liability_basis];
    o.deferred_tax_liability_basis = txt
      ? `${txt} ${asOf ? toDateDisplay(asOf) : "today"}`
      : labelFor(o.deferred_tax_liability_basis);
  }
  if (key === "profit_and_loss" && o.total_revenue === undefined && isNum(o.net_income) && isNum(o.total_expenses)) {
    return {
      total_revenue: Math.round((Number(o.net_income) + Number(o.total_expenses)) * 100) / 100,
      ...o,
    };
  }
  return o;
};

const renderDashboard = (r) => {
  const { currency, as_of, counts, ...rest } = r;
  return (
    <div className="ff-statement">
      <div className="ff-section">
        <div className="ff-kv">
          <span>Currency</span>
          <b>{currency || "-"}</b>
        </div>
        <div className="ff-kv">
          <span>As Of</span>
          <b>{toDateDisplay(as_of)}</b>{/* FF-06 */}
        </div>
      </div>
      {counts && typeof counts === "object" && (
        <KpiGrid
          items={Object.entries(counts).map(([k, v]) => ({
            label: label(k),
            value: fmt(v) === "-" ? "-" : Number(v).toLocaleString(),
          }))}
        />
      )}
      {Object.entries(rest)
        .filter(([, v]) => v && typeof v === "object")
        .map(([k, v]) => (
          <KvSection key={k} title={label(k)} obj={dashboardSection(k, v, as_of)} />
        ))}
    </div>
  );
};

const renderReconciliation = (r) => (
  <div className="ff-statement">
    {Object.entries(r).map(([k, v]) =>
      v && typeof v === "object" ? (
        <KvSection key={k} title={label(k)} obj={v} />
      ) : (
        <div className="ff-kv" key={k}>
          <span>{label(k)}</span>
          <b>{valEl(v, k)}</b>
        </div>
      )
    )}
  </div>
);

// Recursively render whatever shape the statement endpoints return.
const RenderValue = ({ name, value }) => {
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    if (typeof value[0] === "object" && value[0] !== null) {
      const cols = Object.keys(value[0]);
      return (
        <div className="ff-section">
          {name && <p className="ff-section-title">{label(name)}</p>}
          <div className="VA_table">
            <table className="table tableAdmin">
              <thead className="tableHead">
                <tr>
                  {cols.map((c) => (
                    <th key={c}>{label(c)}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="tableBody">
                {value.map((row, i) => (
                  <tr key={i}>
                    {cols.map((c) => (
                      <td key={c}>{cell(c, row[c])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    return (
      <div className="ff-kv">
        <span>{label(name)}</span>
        <b>{value.join(", ")}</b>
      </div>
    );
  }

  if (value && typeof value === "object") {
    return (
      <div className="ff-section">
        {name && <p className="ff-section-title">{label(name)}</p>}
        {Object.entries(value).map(([k, v]) => (
          <RenderValue key={k} name={k} value={v} />
        ))}
      </div>
    );
  }

  return (
    <div className="ff-kv">
      <span>{label(name)}</span>
      <b>{cell(name, value)}</b>
    </div>
  );
};

const TABS = [
  { key: "dashboard", label: "Dashboard", api: "FLEET_FINANCE_STATEMENTS_DASHBOARD", field: "dashboard" },
  { key: "balance", label: "Balance Sheet", api: "FLEET_FINANCE_STATEMENTS_BALANCE_SHEET", field: "report" },
  { key: "income", label: "Income Statement", api: "FLEET_FINANCE_STATEMENTS_INCOME_STATEMENT", field: "report" },
  { key: "cashflow", label: "Cash Flow", api: "FLEET_FINANCE_STATEMENTS_CASH_FLOW", field: "report" },
  { key: "reconciliation", label: "Reconciliation", api: "FLEET_FINANCE_STATEMENTS_RECONCILIATION", field: "report" },
];

// ===== Client-side PDF export (no backend PDF endpoint for statements) =====
const BRAND = [156, 73, 0]; // #9c4900
const BODY_BG = [246, 239, 233]; // #f6efe9

// Preload the brand logo once and cache it as a PNG data URL (jsPDF can't embed
// the raw imported asset directly). Returns { dataUrl, w, h } or null.
let logoCache;
const getLogo = () =>
  new Promise((resolve) => {
    if (logoCache !== undefined) return resolve(logoCache);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        logoCache = {
          dataUrl: canvas.toDataURL("image/png"),
          w: img.naturalWidth,
          h: img.naturalHeight,
        };
      } catch (e) {
        logoCache = null;
      }
      resolve(logoCache);
    };
    img.onerror = () => {
      logoCache = null;
      resolve(null);
    };
    img.src = brandLogo;
  });

// PDF cell: identifiers verbatim, everything else formatted like the screen.
const cellFmt = (key, v) => {
  if (v === null || v === undefined || v === "") return "-";
  if (isCodeKey(key)) return String(v);
  if (isDateKey(key) && /^\d{4}-\d{2}-\d{2}/.test(String(v))) return toDateDisplay(v);
  return fmt(v);
};

const pdfContent = (startY, opts) => ({
  theme: "grid",
  startY,
  headStyles: { fillColor: BRAND, textColor: 255 },
  bodyStyles: { fillColor: BODY_BG, textColor: 20 },
  styles: { fontSize: 9, cellPadding: 5 },
  margin: { left: 40, right: 40 },
  ...opts,
});

// Flatten a (possibly nested) object into [label, value] rows for a KV table.
const kvRows = (obj, prefix = "") => {
  const rows = [];
  Object.entries(obj || {}).forEach(([k, v]) => {
    const name = prefix ? `${prefix} › ${label(k)}` : label(k);
    if (Array.isArray(v)) {
      rows.push([name, v.length && typeof v[0] === "object" ? `${v.length} rows` : v.join(", ")]);
    } else if (v && typeof v === "object") {
      rows.push(...kvRows(v, name));
    } else {
      rows.push([name, cellFmt(k, v)]);
    }
  });
  return rows;
};

const buildStatementPdf = (tab, data, logo) => {
  const doc = new jsPDF("portrait", "pt", "A4");
  const marginLeft = 40;
  const title = data.title || tab.label;
  let titleX = marginLeft;
  let headerBottom = 40;

  // Brand logo (top-left), title beside it.
  if (logo && logo.dataUrl) {
    const h = 30;
    const w = logo.h ? (h * logo.w) / logo.h : h;
    doc.addImage(logo.dataUrl, "PNG", marginLeft, 20, w, h);
    titleX = marginLeft + w + 14;
    headerBottom = 50;
  }

  doc.setFontSize(16);
  doc.text(String(title), titleX, 40);

  let y = headerBottom;
  const meta = [];
  if (data.currency) meta.push(`Currency: ${data.currency}`);
  if (data.as_of) meta.push(`As Of: ${toDateDisplay(data.as_of)}`); // FF-06
  if (meta.length) {
    y += 16;
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(meta.join("      "), marginLeft, y);
    doc.setTextColor(0);
  }
  y += 14;

  const scalarRows = [];
  const sections = [];
  Object.entries(data).forEach(([k, v]) => {
    if (["title", "currency", "as_of"].includes(k)) return;
    if (Array.isArray(v)) {
      if (v.length && typeof v[0] === "object") sections.push({ kind: "table", title: k, rows: v });
      else if (v.length) scalarRows.push([label(k), v.join(", ")]);
    } else if (v && typeof v === "object") {
      sections.push({ kind: "kv", title: k, obj: v });
    } else {
      scalarRows.push([label(k), cellFmt(k, v)]);
    }
  });

  const sectionTitle = (text) => {
    y += 10;
    doc.setFontSize(12);
    doc.setTextColor(...BRAND);
    doc.text(String(text), marginLeft, y);
    doc.setTextColor(0);
    y += 4;
  };

  if (scalarRows.length) {
    doc.autoTable(pdfContent(y + 4, { head: [["Field", "Value"]], body: scalarRows }));
    y = doc.lastAutoTable.finalY + 6;
  }

  sections.forEach((s) => {
    sectionTitle(label(s.title));
    if (s.kind === "table") {
      const cols = Object.keys(s.rows[0]);
      doc.autoTable(
        pdfContent(y + 4, {
          head: [cols.map(label)],
          body: s.rows.map((r) => cols.map((c) => cellFmt(c, r[c]))),
        })
      );
    } else {
      doc.autoTable(pdfContent(y + 4, { head: [["Field", "Value"]], body: kvRows(s.obj) }));
    }
    y = doc.lastAutoTable.finalY + 6;
  });

  doc.save(`${String(title).replace(/\s+/g, "_")}.pdf`);
};

const FinancialStatements = () => {
  const { sidebar } = useContext(AppContext);
  const { t } = useTranslation();

  const [asOf, setAsOf] = useState("");
  const [activeKey, setActiveKey] = useState("dashboard");
  const [cache, setCache] = useState({}); // key -> data
  const [loadingKey, setLoadingKey] = useState("");
  const [pdfKey, setPdfKey] = useState(""); // report currently exporting to PDF

  // Loads a statement and returns its payload (so callers can export it on demand).
  const loadTab = (tab, force = false) => {
    if (!force && cache[tab.key] !== undefined) return Promise.resolve(cache[tab.key]);
    setLoadingKey(tab.key);
    const body = JSON.stringify(asOf ? { as_of: asOf } : {});
    return simplePostCall(ApiConfig[tab.api], body)
      .then((data) => {
        if (data?.success || data?.result) {
          const payload =
            data?.[tab.field] ?? data?.data ?? data?.dashboard ?? data?.report ?? {};
          setCache((prev) => ({ ...prev, [tab.key]: payload }));
          return payload;
        }
        notifyError(data?.message || data?.error || "Unable to load statement");
        setCache((prev) => ({ ...prev, [tab.key]: null }));
        return null;
      })
      .catch((err) => {
        console.log("statement err", err);
        notifyError("Something went wrong");
        setCache((prev) => ({ ...prev, [tab.key]: null }));
        return null;
      })
      .finally(() => setLoadingKey(""));
  };

  useEffect(() => {
    const tab = TABS.find((x) => x.key === activeKey);
    if (tab) loadTab(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  // FF-05: a new as-of date invalidates every cached statement and reloads
  // the active tab; other tabs reload on their next click.
  const prevAsOf = useRef(asOf);
  useEffect(() => {
    if (prevAsOf.current === asOf) return;
    prevAsOf.current = asOf;
    setCache({});
    const tab = TABS.find((x) => x.key === activeKey);
    if (tab) loadTab(tab, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asOf]);

  // FF-05: Refresh reloads the active tab with the current as_of date
  const refreshAll = () => {
    setCache({});
    const tab = TABS.find((x) => x.key === activeKey);
    if (tab) loadTab(tab, true);
  };

  const isEmpty = (d) =>
    !d || (typeof d === "object" && Object.keys(d).length === 0);

  // Fetch (if needed) then export the given report as a PDF.
  const downloadReport = async (tab) => {
    setPdfKey(tab.key);
    try {
      const data = cache[tab.key] ?? (await loadTab(tab));
      if (isEmpty(data)) {
        notifyError(t("No data available to export"));
        return;
      }
      const logo = await getLogo();
      buildStatementPdf(tab, data, logo);
      notifySuccess(t("PDF downloaded"));
    } catch (err) {
      console.log("pdf export err", err);
      notifyError(t("Unable to generate PDF"));
    } finally {
      setPdfKey("");
    }
  };

  const renderTab = (tab) => {
    const data = cache[tab.key];
    if (loadingKey === tab.key) return <Loader />;
    if (data === undefined) return null;
    if (!data || (typeof data === "object" && Object.keys(data).length === 0))
      return <NoDataComp />;

    switch (tab.key) {
      case "income":
        return <IncomeStatement r={data} />;
      case "balance":
        return <BalanceSheet r={data} />;
      case "cashflow":
        return renderCashFlow(data);
      case "dashboard":
        return renderDashboard(data);
      case "reconciliation":
        return renderReconciliation(data);
      default:
        return (
          <div className="ff-statement">
            <RenderValue name="" value={data} />
          </div>
        );
    }
  };

  return (
    <motion.div
      className={sidebar ? "taskMain " : "cx-active taskMain"}
      id="cx-main"
      variants={animations}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.1 }}
    >
      <div id="cx-wrapper" className="Fleet_Finance_Statements">
        <div className="main-master-wrapper">
          <PageHead
            title={t("Financial Statements")}
            subtitle={t("GL-driven balance sheet, P&L, cash flow & KPIs")}
          >
            <div className="form_input_main" style={{ marginBottom: 0 }}>
              {/* FF-05: changing the date clears the cache (see asOf effect) */}
              <Form.Control
                type="date"
                value={asOf}
                onChange={(e) => setAsOf(e.target.value)}
                title={t("As of date")}
              />
            </div>
            <button
              className="cx-btn-1"
              onClick={() => {
                const tab = TABS.find((x) => x.key === activeKey);
                if (tab) downloadReport(tab);
              }}
              disabled={pdfKey === activeKey}
            >
              {pdfKey === activeKey ? (
                <Spinner animation="border" size="sm" />
              ) : (
                t("Download PDF")
              )}
            </button>
            <button className="cx-btn-2" onClick={refreshAll}>
              {t("Refresh")}
            </button>
          </PageHead>

          <Tab.Container activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
            <Nav variant="pills" className="td-nav">
              {TABS.map((tab) => (
                <Nav.Item className="td-tab" key={tab.key}>
                  <Nav.Link className="td-link" eventKey={tab.key}>
                    {t(tab.label)}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
            <Col sm={12} className="mt-3">
              <Tab.Content>
                {TABS.map((tab) => (
                  <Tab.Pane eventKey={tab.key} key={tab.key}>
                    {activeKey === tab.key && renderTab(tab)}
                  </Tab.Pane>
                ))}
              </Tab.Content>
            </Col>
          </Tab.Container>
        </div>
      </div>
    </motion.div>
  );
};

export default FinancialStatements;
